import { Fragment, type ReactNode } from "react";

interface SimpleMarkdownProps {
  text: string;
  className?: string;
}

const INLINE_TOKEN_REGEX = /(`[^`]+`|\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*)/g;

function getSafeHref(rawHref: string): string | null {
  const href = rawHref.trim();
  if (!href) return null;

  try {
    const url = new URL(href);
    if (url.protocol === "http:" || url.protocol === "https:" || url.protocol === "mailto:") {
      return href;
    }
    return null;
  } catch {
    return null;
  }
}

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(INLINE_TOKEN_REGEX)) {
    const fullMatch = match[0];
    if (!fullMatch) continue;

    const matchIndex = match.index ?? 0;
    if (matchIndex > lastIndex) {
      nodes.push(text.slice(lastIndex, matchIndex));
    }

    const key = `${keyPrefix}-${matchIndex}`;

    if (fullMatch.startsWith("`") && fullMatch.endsWith("`")) {
      nodes.push(
        <code key={key} className="rounded bg-zinc-800 px-1 py-0.5 text-[11px] font-mono">
          {fullMatch.slice(1, -1)}
        </code>,
      );
    } else if (match[2] && match[3]) {
      const safeHref = getSafeHref(match[3]);
      const label = renderInline(match[2], `${key}-label`);
      if (safeHref) {
        nodes.push(
          <a
            key={key}
            href={safeHref}
            target="_blank"
            rel="noopener noreferrer"
            className="text-schema-link underline hover:text-schema-link-hover"
          >
            {label}
          </a>,
        );
      } else {
        nodes.push(<Fragment key={key}>{label}</Fragment>);
      }
    } else if (match[4]) {
      nodes.push(<strong key={key}>{renderInline(match[4], `${key}-strong`)}</strong>);
    } else if (match[5]) {
      nodes.push(<em key={key}>{renderInline(match[5], `${key}-em`)}</em>);
    } else {
      nodes.push(fullMatch);
    }

    lastIndex = matchIndex + fullMatch.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}

export function SimpleMarkdown({ text, className }: SimpleMarkdownProps) {
  const lines = text.split("\n");

  return (
    <div className={className}>
      {lines.map((rawLine, index) => {
        const line = rawLine.trimEnd();
        const key = `line-${index}`;

        if (/^-{3,}$/.test(line.trim())) {
          return <hr key={key} className="my-1 border-zinc-700" />;
        }

        const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
        if (headingMatch) {
          const level = headingMatch[1]!.length;
          const headingClass = level <= 2 ? "mt-1 text-xs font-semibold text-zinc-300" : "mt-1 text-[11px] font-semibold text-zinc-300";
          return (
            <div key={key} className={headingClass}>
              {renderInline(headingMatch[2]!, `${key}-heading`)}
            </div>
          );
        }

        if (/^\s*[-*]\s+/.test(line)) {
          const content = line.replace(/^\s*[-*]\s+/, "");
          return (
            <div key={key} className="flex gap-1">
              <span className="shrink-0">·</span>
              <span>{renderInline(content, `${key}-list`)}</span>
            </div>
          );
        }

        if (line.trim() === "") {
          return <div key={key} className="h-1" />;
        }

        return <div key={key}>{renderInline(line, `${key}-paragraph`)}</div>;
      })}
    </div>
  );
}
